/**
 * Pure validator + normalizer for `fg.tour.v1` documents.
 *
 * One implementation serves three callers, which is the point: the authoring
 * rail runs it on every keystroke, the publish endpoint runs it before writing
 * `status = 'published'`, and the unit tests run it over every committed
 * `src/tours/*.json`. A rule that lives in only one of those three drifts.
 *
 * Everything here is pure — no DOM, no fetch, no clock — so it is cheap enough
 * to call on each render and trivially testable.
 */

import { CFO_WORKER_REQUIRED_PAGE_IDS, isCfoPageSlug } from '../parity-dev/cfo/cfoRoutes'
import {
  TOUR_SCHEMA_VERSION,
  flattenTourSteps,
  type TourAnchor,
  type TourCta,
  type TourDoc,
  type TourStep,
} from './tourTypes'

export type TourIssueLevel = 'error' | 'warning'

export type TourIssue = {
  level: TourIssueLevel
  /** Dotted path into the document, e.g. `chapters[1].steps[2].anchor`. */
  path: string
  message: string
  code: string
}

export type TourValidation = {
  ok: boolean
  errors: TourIssue[]
  warnings: TourIssue[]
}

/** Storylane's own guidance, and it holds up: short tours finish. */
export const MAX_STEPS_PER_TOUR = 12
export const MIN_STEPS_PER_CHAPTER = 1
export const MAX_STEPS_PER_CHAPTER = 10
export const MAX_CHAPTERS = 4

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/
const ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/
/** `{{token}}` / `{{fact:gross_margin_pct}}` / `{{$M/d/yyyy|+5d}}`. */
const TOKEN_RE = /\{\{\s*([^}]+?)\s*\}\}/g

const WORKER_REQUIRED = new Set<string>(CFO_WORKER_REQUIRED_PAGE_IDS)

const err = (code: string, path: string, message: string): TourIssue => ({ level: 'error', code, path, message })
const warn = (code: string, path: string, message: string): TourIssue => ({ level: 'warning', code, path, message })

/** Every free-text field on a step that may carry tokens. */
function stepText(step: TourStep): string[] {
  const w = step.widget
  const out: string[] = [step.title]
  if (w.kind === 'tooltip') out.push(w.title || '', w.body)
  else if (w.kind === 'hotspot') out.push(w.body || '')
  else if (w.kind === 'text_modal') out.push(w.title, w.body)
  else if (w.kind === 'media_modal') out.push(w.title || '', w.body || '', w.media.alt)
  else if (w.kind === 'lead_form') out.push(w.title, w.body)
  else if (w.kind === 'their_numbers') out.push(w.title, w.body ?? '')
  ctasOf(step).forEach((cta) => out.push(cta.label))
  return out.filter(Boolean)
}

function ctasOf(step: TourStep): TourCta[] {
  const w = step.widget
  if (w.kind === 'lead_form' || w.kind === 'their_numbers' || w.kind === 'none' || w.kind === 'hotspot') return []
  return w.ctas || []
}

/** Walk an anchor and its `within` chain. */
function anchorChain(anchor: TourAnchor | null | undefined): TourAnchor[] {
  const out: TourAnchor[] = []
  let cur: TourAnchor | null | undefined = anchor
  // `within` nesting is authored, not recursive data — cap it so a hand-edited
  // document with a cycle cannot hang the editor.
  for (let depth = 0; cur && depth < 8; depth += 1) {
    out.push(cur)
    cur = cur.within
  }
  return out
}

export function validateTourDoc(doc: TourDoc): TourValidation {
  const errors: TourIssue[] = []
  const warnings: TourIssue[] = []

  if (doc?.schemaVersion !== TOUR_SCHEMA_VERSION) {
    errors.push(err('schema_version', 'schemaVersion', `Expected ${TOUR_SCHEMA_VERSION}.`))
    return { ok: false, errors, warnings }
  }
  if (!SLUG_RE.test(String(doc.slug || ''))) {
    errors.push(err('slug', 'slug', 'Slug must be lowercase letters, digits and dashes (2-63 chars).'))
  }
  if (!String(doc.name || '').trim()) {
    errors.push(err('name', 'name', 'A tour needs a name.'))
  }
  // Must list every member of TourContext. 'firm' was missing while the type,
  // the per-step rules below, useTourEngine and verify-anchors all supported it
  // — so a firm-cockpit tour collected a context error, loadBundledTour returned
  // null, and it failed SILENTLY at load rather than loudly at build.
  if (!['demo', 'embed', 'onboarding', 'client', 'firm'].includes(doc.context)) {
    errors.push(err('context', 'context', 'Unknown context.'))
  }

  const chapters = Array.isArray(doc.chapters) ? doc.chapters : []
  if (chapters.length === 0) {
    errors.push(err('chapters_empty', 'chapters', 'A tour needs at least one chapter.'))
  }
  if (chapters.length > MAX_CHAPTERS) {
    warnings.push(warn('chapters_many', 'chapters', `${chapters.length} chapters; ${MAX_CHAPTERS} or fewer reads better.`))
  }

  if (doc.theme?.accent && !/^#[0-9a-fA-F]{3,8}$/.test(doc.theme.accent)) {
    warnings.push(warn('theme_accent', 'theme.accent', 'Accent should be a hex colour; ignoring it.'))
  }

  const refs = flattenTourSteps(doc)
  if (refs.length > MAX_STEPS_PER_TOUR) {
    warnings.push(warn('steps_many', 'chapters', `${refs.length} steps; tours over ${MAX_STEPS_PER_TOUR} lose people.`))
  }

  // Entry route. Scoped by context exactly as the per-step rules below are:
  // 'firm' and 'client' surfaces are not CFO pages, and applying the CFO enum
  // to their entry route rejects the whole document. That is not hypothetical —
  // `client-month-close` shipped with narration audio and never loaded once,
  // because its legitimate entry page 'today' is not in the CFO enum and
  // `loadBundledTour` returns null for any doc with errors.
  const entryPage = String(doc.entry?.route?.page || '')
  if (doc.context === 'firm') {
    if (entryPage !== 'firm') {
      errors.push(err('entry_route', 'entry.route.page', "A firm-cockpit tour must enter on page 'firm'."))
    }
  } else if (doc.context === 'client') {
    if (!/^[a-z0-9][a-z0-9_-]{0,39}$/.test(entryPage)) {
      errors.push(err('entry_route', 'entry.route.page', 'Client-workspace tour needs a lowercase entry page id.'))
    }
  } else if (!isCfoPageSlug(doc.entry?.route?.page)) {
    errors.push(err('entry_route', 'entry.route.page', 'Entry route is not a CFO page.'))
  }

  const seenStepIds = new Set<string>()
  const seenChapterIds = new Set<string>()
  const declaredTokens = new Set((doc.tokens || []).map((t) => t.key))

  ;(doc.tokens || []).forEach((token, i) => {
    const path = `tokens[${i}]`
    // Plain identifiers, or the fact: namespace for governed numbers — the
    // resolver treats fact tokens as live values with this declared default.
    const bareKey = String(token.key || '').replace(/^fact:/, '')
    if (!ID_RE.test(bareKey)) {
      errors.push(err('token_key', `${path}.key`, 'Token keys are lowercase identifiers, optionally namespaced fact:<key>.'))
    }
    // A missing default is how literal `{{first_name}}` reaches a prospect.
    if (typeof token.defaultValue !== 'string' || !token.defaultValue.trim()) {
      errors.push(err('token_default', `${path}.defaultValue`, `Token "${token.key}" needs a default so it never renders blank.`))
    }
  })

  chapters.forEach((chapter, ci) => {
    const cpath = `chapters[${ci}]`
    if (!ID_RE.test(String(chapter.id || ''))) {
      errors.push(err('chapter_id', `${cpath}.id`, 'Chapter id must be a lowercase identifier.'))
    } else if (seenChapterIds.has(chapter.id)) {
      errors.push(err('chapter_dupe', `${cpath}.id`, `Duplicate chapter id "${chapter.id}".`))
    } else {
      seenChapterIds.add(chapter.id)
    }
    if (!String(chapter.title || '').trim()) {
      errors.push(err('chapter_title', `${cpath}.title`, 'Chapter needs a title.'))
    }

    const steps = Array.isArray(chapter.steps) ? chapter.steps : []
    if (steps.length < MIN_STEPS_PER_CHAPTER) {
      errors.push(err('chapter_empty', `${cpath}.steps`, 'Chapter has no steps.'))
    }
    if (steps.length > MAX_STEPS_PER_CHAPTER) {
      warnings.push(warn('chapter_long', `${cpath}.steps`, `${steps.length} steps in one chapter; split it.`))
    }

    steps.forEach((step, si) => {
      const spath = `${cpath}.steps[${si}]`
      if (!ID_RE.test(String(step.id || ''))) {
        errors.push(err('step_id', `${spath}.id`, 'Step id must be a lowercase identifier.'))
      } else if (seenStepIds.has(step.id)) {
        // Step ids are the analytics primary key — a duplicate silently merges
        // two funnels into one and the drop-off chart lies.
        errors.push(err('step_dupe', `${spath}.id`, `Duplicate step id "${step.id}".`))
      } else {
        seenStepIds.add(step.id)
      }

      if (doc.context === 'firm') {
        // The firm cockpit is a single page in its own route mode — there is
        // nowhere to navigate to, so the only legal page is 'firm'. Anything
        // else would send the engine down the CFO routing path and eject an
        // advisor out of the surface the tour is about.
        if (String(step.route?.page || '') !== 'firm') {
          errors.push(err('step_route', `${spath}.route.page`, "Firm-cockpit steps must use page 'firm'."))
        }
      } else if (doc.context === 'client') {
        // Client-workspace pages are spec-defined ids, not the CFO enum. The
        // page may not exist in a given firm's spec — that is what per-step
        // fallback:'skip' is for — but the id itself must be shaped like one.
        if (!/^[a-z0-9][a-z0-9_-]{0,39}$/.test(String(step.route?.page || ''))) {
          errors.push(err('step_route', `${spath}.route.page`, 'Client-workspace step needs a lowercase page id.'))
        }
      } else if (!isCfoPageSlug(step.route?.page)) {
        errors.push(err('step_route', `${spath}.route.page`, 'Step route is not a CFO page.'))
      } else if (doc.context === 'embed' && WORKER_REQUIRED.has(step.route.page)) {
        // An embedded iframe is never cross-origin isolated, so SharedArrayBuffer
        // is gone and the DuckDB worker never boots. Catch it at publish rather
        // than as a spinner on the homepage.
        errors.push(err(
          'embed_worker_page',
          `${spath}.route.page`,
          `"${step.route.page}" needs the in-browser worker, which an embedded tour cannot have. Use a full-page share link instead.`,
        ))
      }

      if (!['modal', 'skip', 'block'].includes(step.fallback)) {
        errors.push(err('step_fallback', `${spath}.fallback`, 'Every step needs a fallback so a missing anchor cannot break the demo.'))
      }

      // Anchor rules.
      const needsAnchor = step.widget.kind === 'tooltip' || step.widget.kind === 'hotspot'
      if (needsAnchor && !step.anchor) {
        errors.push(err('anchor_required', `${spath}.anchor`, `A ${step.widget.kind} needs an anchor.`))
      }
      anchorChain(step.anchor).forEach((anchor, ai) => {
        const apath = ai === 0 ? `${spath}.anchor` : `${spath}.anchor${'.within'.repeat(ai)}`
        if (!String(anchor.label || '').trim()) {
          errors.push(err('anchor_label', `${apath}.label`, 'Anchors need a label — it is what appears in the drift alert.'))
        }
        if (anchor.kind === 'css') {
          if (!doc.allowBrittleAnchors) {
            errors.push(err(
              'anchor_brittle',
              `${apath}`,
              `"${anchor.label}" uses a raw CSS path, which breaks on the next refactor. Ask an engineer for a data-fg-tour attribute, or record an explicit override.`,
            ))
          } else {
            warnings.push(warn('anchor_brittle_allowed', `${apath}`, `"${anchor.label}" is a brittle CSS path running on an override.`))
          }
        }
        if (anchor.kind === 'nav' && !isCfoPageSlug(anchor.value)) {
          errors.push(err('anchor_nav', `${apath}.value`, 'Nav anchor must name a CFO page.'))
        }
      })

      if (step.advance?.kind === 'click_anchor' && !step.anchor) {
        errors.push(err('advance_click', `${spath}.advance`, 'click_anchor needs an anchor to click.'))
      }
      if (step.advance?.kind === 'timeout' && !(Number(step.advance.ms) > 0)) {
        errors.push(err('advance_timeout', `${spath}.advance.ms`, 'Timeout must be a positive number of milliseconds.'))
      }

      // Tokens referenced but never declared render as their own braces.
      stepText(step).forEach((text) => {
        for (const match of String(text).matchAll(TOKEN_RE)) {
          const raw = match[1]
          if (raw.startsWith('$')) continue // date token, self-describing
          const key = raw.startsWith('fact:') ? raw : raw.split('|')[0].trim()
          if (!declaredTokens.has(key)) {
            // fact tokens too: undeclared means no default, and no default
            // means an empty hole in the sentence until the cockpit fetch
            // lands — or forever, when the contract can't supply the fact.
            errors.push(err('token_undeclared', `${spath}`, `Uses {{${key}}} but no such token is declared.`))
          }
        }
      })
    })
  })

  // Cross-step references resolve only once every id is known.
  refs.forEach(({ step, index }) => {
    ctasOf(step).forEach((cta, i) => {
      const path = `steps[${index}].ctas[${i}]`
      if (cta.action.kind === 'goto_step' && !seenStepIds.has(cta.action.stepId)) {
        errors.push(err('cta_goto', path, `CTA jumps to "${cta.action.stepId}", which is not a step in this tour.`))
      }
      if (cta.action.kind === 'external_url' && !/^https?:\/\//i.test(cta.action.url)) {
        errors.push(err('cta_url', path, 'External CTA needs an absolute http(s) URL.'))
      }
      if (!String(cta.label || '').trim()) {
        errors.push(err('cta_label', path, 'CTA needs a label.'))
      }
    })
    if (step.advance?.kind === 'element_appears') {
      anchorChain(step.advance.anchor).forEach((a, ai) => {
        if (!String(a.label || '').trim()) {
          errors.push(err('anchor_label', `steps[${index}].advance.anchor${'.within'.repeat(ai)}.label`, 'Advance anchor needs a label.'))
        }
      })
    }
  })

  ;(doc.checklist?.items || []).forEach((item, i) => {
    if (!seenStepIds.has(item.stepId)) {
      errors.push(err('checklist_step', `checklist.items[${i}].stepId`, `Checklist points at "${item.stepId}", which is not a step in this tour.`))
    }
  })

  if (doc.convertCta) {
    const a = doc.convertCta.action
    if (a.kind === 'goto_step' && !seenStepIds.has(a.stepId)) {
      errors.push(err('cta_goto', 'convertCta', `Convert CTA jumps to "${a.stepId}", which is not a step in this tour.`))
    }
    if (a.kind === 'external_url' && !/^https?:\/\//i.test(a.url)) {
      errors.push(err('cta_url', 'convertCta', 'Convert CTA needs an absolute http(s) URL.'))
    }
  }

  return { ok: errors.length === 0, errors, warnings }
}

/**
 * Parse an untrusted document (bundled JSON, an API payload, an editor draft).
 * Returns `null` rather than throwing so every caller degrades to the next
 * source instead of blanking the page.
 */
export function parseTourDoc(input: unknown): { doc: TourDoc; validation: TourValidation } | null {
  if (!input || typeof input !== 'object') return null
  const doc = input as TourDoc
  const validation = validateTourDoc(doc)
  if (!validation.ok) return { doc, validation }
  return { doc, validation }
}
